import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { SessionItem, type SessionPathname } from './SessionItem';
import { VirtualizedSessionList } from './VirtualizedSessionList';

// Re-defining Session interface here or import from a shared types file
interface Session {
  sess_id: number;
  session_id: string;
  title: string;
  timestamp: number;
  isActive?: boolean;
  messagesFetched: boolean;
  access_role?: 'owner' | 'shared_direct' | 'shared_group';
  org_id?: string | null;
}

interface SessionListProps {
  groupedSessions: Record<string, Session[]>;
  groupOrder: string[];
  showContextBadge?: boolean;
  onSessionClick?: (() => void) | undefined;
  /** Destination route for each session (chat | search). Defaults to chat. */
  sessionPathname?: SessionPathname | undefined;
  /**
   * Render the per-group date headers (Today / Yesterday / …). Defaults to
   * true. When false the list is flat chronological (the "group by: none"
   * mode) — the groups are still walked in `groupOrder`, which is already
   * timestamp-descending, so hiding the headers yields one flat list.
   */
  showGroupHeaders?: boolean | undefined;
}

// Threshold for enabling virtualization (number of sessions)
const VIRTUALIZATION_THRESHOLD = 30;

export const SessionList = ({
  groupedSessions,
  groupOrder,
  showContextBadge,
  onSessionClick,
  sessionPathname,
  showGroupHeaders = true,
}: SessionListProps) => {
  const t = useTranslations('Common');
  const hasSessions = Object.keys(groupedSessions).length > 0;

  // Count total sessions
  const totalSessions = Object.values(groupedSessions).reduce(
    (sum, sessions) => sum + sessions.length,
    0
  );

  // Use virtualization for large lists
  const shouldVirtualize = totalSessions > VIRTUALIZATION_THRESHOLD;

  if (!hasSessions) {
    return (
      <div className='flex-grow overflow-y-auto flex justify-center items-center h-[80vh] text-center text-content-muted'>
        <span className='text-sm'>{t('noChatHistoryFound')}</span>
      </div>
    );
  }

  // Use virtualized version for large lists
  if (shouldVirtualize) {
    return (
      <VirtualizedSessionList
        groupedSessions={groupedSessions}
        groupOrder={groupOrder}
        showContextBadge={showContextBadge ?? false}
        onSessionClick={onSessionClick}
        sessionPathname={sessionPathname}
        showGroupHeaders={showGroupHeaders}
      />
    );
  }

  // Non-virtualized version for smaller lists
  return (
    <div className='ml-2'>
      <ul className='space-y-0.5 pt-1 pb-1'>
        {groupOrder.map(
          (groupName) =>
            groupedSessions[groupName] && (
              // Keyed Fragment, not a wrapper <div>: in flat ("group by: none")
              // mode the header is hidden, so a per-group <div> would just emit
              // empty wrapper nodes. The Fragment groups the (optional) header +
              // items with a stable key and no DOM noise.
              <Fragment key={groupName}>
                {/* Sticky group header (hidden in flat "group by: none" mode) */}
                {showGroupHeaders && (
                  <h4 className='sticky top-0 bg-surface-sidebar text-xs font-medium text-content-muted mt-1 mb-0 px-1.5 py-0.5 z-10'>
                    {groupName}
                  </h4>
                )}
                {/* Session items for the group */}
                {groupedSessions[groupName].map((session) => (
                  <SessionItem
                    key={session.session_id}
                    session={session}
                    showContextBadge={showContextBadge ?? false}
                    onSessionClick={onSessionClick}
                    sessionPathname={sessionPathname}
                  />
                ))}
              </Fragment>
            )
        )}
      </ul>
    </div>
  );
};
